/** @jsx jsx */
import { colors } from '@apollo/brand';
import { gql, useMutation } from '@apollo/client';
import IconExplorer from '@apollo/icons/default/IconExplorer.svg';
import IconStar from '@apollo/icons/default/IconStar.svg';
import { Avatar, Button, Card, CardBody, IconButton } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { Link, useLocation } from 'react-router-dom';

import { MarbledAvatar } from 'src/components/common/MarbledAvatar';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useIdentity } from 'src/hooks/useIdentity';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { DescriptionMarkdownRenderer } from '../graph/documentationPage/schemaPage/descriptionMarkdownRenderer/DescriptionMarkdownRenderer';
import { variantRouteConfig } from '../graph/routes';

const FAVORITE_VARIANT = gql`
  mutation AtlasFavoriteGraphVariant(
    $setIsFavoriteOfCurrentUserFavorite: Boolean!
    $graphVariant: String!
    $graphId: ID!
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        setIsFavoriteOfCurrentUser(
          favorite: $setIsFavoriteOfCurrentUserFavorite
        ) {
          id
          name
          isFavoriteOfCurrentUser
          graph {
            id
          }
        }
      }
    }
  }
`;

export const GraphCard = ({
  variant,
}: {
  variant: GraphQLTypes.AtlasLayoutQuery_publiclyListedVariants;
}) => {
  const location = useLocation();
  const { me } = useIdentity();

  const [setFavorite, { loading: favoriteLoading }] = useMutation<
    GraphQLTypes.AtlasFavoriteGraphVariant,
    GraphQLTypes.AtlasFavoriteGraphVariantVariables
  >(FAVORITE_VARIANT);

  return (
    <Link
      to={variantRouteConfig.location({
        graphVisibilityType: 'public',
        graphId: variant.graph.id,
        graphVariant: variant.name,
      })}
      className="flex"
    >
      <Card className="max-h-56 min-h-48 w-full overflow-hidden transition-all hover:scale-105">
        <CardBody className="p-0 first:p-0 last:p-0">
          {/* graph card header */}
          <div
            className="flex flex-none p-3 px-4 text-white"
            css={css`
              background: linear-gradient(
                90deg,
                ${colors.tokens.brand.primary.dark} 0,
                ${colors.tokens.brand.secondary.base} 15%,
                ${colors.tokens.brand.secondary.base} 85%,
                ${colors.tokens.brand.primary.dark} 100%
              );
            `}
          >
            {/* graph avatar */}
            <div>
              {/* check for graph avatar, otherwise show graphql icon */}
              {variant.graph.avatarUrl ? (
                <Avatar
                  variant="graph"
                  src={variant.graph.avatarUrl}
                  name={variant.graph.title}
                  title={variant.graph.title}
                />
              ) : (
                <MarbledAvatar
                  animate={true}
                  size={78}
                  identifier={variant.graph.id}
                />
              )}
            </div>
            {/* graph details */}
            <div className="ml-4 pt-2 text-white">
              {variant.graph.title && (
                <div className="text-2xl font-semibold">
                  {variant.graph.title}
                </div>
              )}
              {variant.activeSchemaPublish && (
                <div className="mb-2 text-sm">
                  <span className="font-semibold">
                    {variant.activeSchemaPublish.schema.typeCount.toLocaleString()}
                  </span>{' '}
                  types •{' '}
                  <span className="font-semibold">
                    {variant.activeSchemaPublish.schema.fieldCount.toLocaleString()}
                  </span>{' '}
                  fields
                </div>
              )}
            </div>
          </div>
          {/* graph description */}
          <div
            className={classnames(
              'mx-4 mb-2 mt-6 overflow-hidden text-sm text-secondary',
            )}
            css={css`
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow-y: hidden;
            `}
          >
            {variant.graph.description && (
              <DescriptionMarkdownRenderer
                descriptionMarkdown={variant.graph.description}
                typeMap={{}}
              />
            )}
          </div>

          {/* footer row */}
          <div className="mx-3 mb-3 mt-auto flex items-center justify-center">
            {/* favorites */}
            {me && (
              <Tooltip
                label={
                  variant.isFavoriteOfCurrentUser
                    ? 'Remove this graph from your favorites'
                    : 'Add this graph to your favorites'
                }
                placement="top-start"
              >
                <IconButton
                  size="sm"
                  aria-label="Add to favorites"
                  className={classnames(
                    variant.isFavoriteOfCurrentUser
                      ? '!text-icon-change'
                      : 'text-icon-primary',
                  )}
                  icon={<IconStar fill="currentColor" />}
                  isLoading={favoriteLoading}
                  variant="hidden"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    setFavorite({
                      variables: {
                        graphId: variant.graph.id,
                        graphVariant: variant.name,
                        setIsFavoriteOfCurrentUserFavorite:
                          !variant.isFavoriteOfCurrentUser,
                      },
                    });
                  }}
                />
              </Tooltip>
            )}

            {/* open dora in modal */}
            <Button
              className="ml-auto"
              as={Link}
              to={setOverlayVisibleLocation({
                location,
                overlayName: Config.modals.atlasGraphPeek,
                additionalSearchParameters: {
                  [Config.queryParameters.GraphRef]:
                    `${variant.graph.id}@${variant.name}`,
                },
              })}
              rightIcon={<IconExplorer />}
            >
              Run a query
            </Button>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
};
